import { useDownloads } from "@/components/downloads";
// Shell owns navigation rendering. Its frame is also used by the isolated,
// fixture-backed review workspace; authentication and queries stay in Shell.
import { Fragment, lazy, Suspense, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { PortalRecovery } from "@/components/recovery/PortalRecovery";
import { resetTips, useTipsEnabled } from "@/components/tips";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { api, fetchClient, type components } from "@/api/client";
import { body } from "@/api/call";
import { ToolbarPopover } from "@/components/ToolbarPopover";
import { PanelResize, usePanelWidth, type PanelSnap } from "@/components/PanelResize";
import { Select } from "@/components/Select";
import { recentProjects, rememberProject, orderProjects } from "@/lib/recent-projects";
import { ProjectMosaic } from "@/components/ProjectMosaic";
import { Failed, Loading } from "@/components/kit";
import { Glyph, Icon, IconFamily, type IconName } from "@/components/icons";
import { CeliaBrand } from "@/components/CeliaBrand";
import { AppearanceToggle } from "./AppearanceToggle";
import { DATA_NAV, MODEL_NAV, OPERATIONS_NAV, PREPARE_NAV, destination, type NavigationItem, type PreviewNavigation } from "./navigation";
import { projectTrail, type Breadcrumb } from "./breadcrumbs";
import { exactTime, relativeTime } from "@/lib/format";
import "./upload-preparation-shell.css";
import "./platform-shell.css";
import "./account-menu.css";
import "./project-navigation.css";

// A route's navigation destination and breadcrumb label come from its first segment after /p/:slug (or the first
// segment of a project-less route such as /jobs/:id), with an exact legacy training alias; never a substring match. So label exports
// highlight Merge plans, receipts highlight Reviews, every /lineage route highlights Lineage, and a dataset named
// "uploads" or a path containing "/datasets" further down cannot select another destination.
type Section = { nav?: NavigationItem["id"]; label: string };
const SECTIONS: Readonly<Record<string, Section>> = {
  datasets: { nav: "datasets", label: "Datasets" },
  subsets: { nav: "subsets", label: "Subsets" }, views: { nav: "views", label: "Views" }, uploads: { nav: "uploads", label: "Uploads" },
  "upload-sessions": { nav: "uploads", label: "Uploads" }, "merge-plans": { nav: "changes", label: "Merge plans" },
  "label-exports": { nav: "changes", label: "Label Studio exports" }, reviews: { nav: "reviews", label: "Reviews" },
  receipts: { nav: "reviews", label: "Receipts" }, lineage: { nav: "lineage", label: "Lineage" },
  workflows: { nav: "workflows", label: "Workflows" },
  audit: { label: "Audit log" }, deletions: { label: "Deletion requests" },
  models: { nav: "models", label: "Models" }, jobs: { nav: "jobs", label: "Jobs & Runs" },
  train: { nav: "train", label: "Train a model" },
  inference: { nav: "inference", label: "Inference & Test" },
  "training-records": { nav: "lineage", label: "Training record" },
  "model-comparison": { nav: "models", label: "Compare models" },
  "model-return": { nav: "models", label: "Upload a trained model" },
  "model-factory": { nav: "models", label: "Models" },
};

function routeSection(pathname: string): Section | undefined {
  const parts = pathname.split("/").filter(Boolean);
  const inProject = parts[0] === "p";
  const sectionParts = inProject ? parts.slice(2) : parts;
  const [first, second, third] = sectionParts;
  if (!first) return undefined;
  if (inProject && first === "settings") return { label: "Project settings" };
  if (inProject && first === "models" && second && third === "train" && sectionParts.length === 3) return SECTIONS.train;
  const section = Object.prototype.hasOwnProperty.call(SECTIONS, first) ? SECTIONS[first] : undefined;
  return section && first === "lineage" && second === "graph" ? { ...section, label: "Lineage graph" } : section;
}

type Project = { slug: string; name: string; description?: string | null; role?: string };
type ShellFrameProps = {
  project?: Project;
  projects?: Project[];
  projectState?: "loading" | "error" | "ready";
  onRetryProjects?: () => void;
  user: { username: string; is_admin: boolean };
  canViewMonitoring?: boolean;
  unread?: number;
  notifications?: readonly components["schemas"]["InboxItemOut"][];
  notificationState?: "loading" | "error" | "ready";
  onRetryNotifications?: () => void;
  children: ReactNode;
  preview?: PreviewNavigation;
  presentation?: "upload-preparation";
  breadcrumbs?: readonly Breadcrumb[];
  toolbarControls?: ReactNode;
  onSignOut?: () => Promise<void>;
  /** Applies the reviewed compact Liquid Glass spine to legacy page content. */
  spine?: boolean;
};

// The navigation collapses to an icon rail when its labels no longer fit: dragged or stepped below the text minimum,
// or on a 1024–1199px viewport. The icon rail expands over the workspace on hover/focus, without shifting content.
const NAV_RAIL = 64, NAV_TEXT_MIN = 184, NAV_COLLAPSE = 128;
const snapNavigation: PanelSnap = (next, current, source) => {
  if (source === "key") return next < current
    ? (current <= NAV_TEXT_MIN ? NAV_RAIL : Math.max(next, NAV_TEXT_MIN))
    : (current < NAV_TEXT_MIN ? NAV_TEXT_MIN : next);
  return next < NAV_COLLAPSE ? NAV_RAIL : Math.max(next, NAV_TEXT_MIN);
};
const RAIL_VIEWPORT = "(min-width: 1024px) and (max-width: 1199px)";
function useRailViewport(): boolean {
  return useSyncExternalStore((change) => {
    const query = window.matchMedia(RAIL_VIEWPORT);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, () => window.matchMedia(RAIL_VIEWPORT).matches, () => false);
}
type NavigationMode = "auto" | "expanded" | "icons";

function navigationBadge(value: NavigationItem["badge"]): ReactNode {
  if (!value) return undefined;
  return <span className="shell-nav-badge">{value === "coming-soon" ? "Coming soon" : "Beta"}</span>;
}

function Item({ to, end, icon, children, badge, testid, active, description }: {
  to: string; end?: boolean; icon: IconName; children: string;
  badge?: ReactNode; testid?: string; active?: boolean; description?: string;
}) {
  const { pathname } = useLocation();
  const selected = active ?? (pathname === to || (!end && pathname.startsWith(`${to}/`)));
  const link = <Link to={to} title={description} data-testid={testid} aria-current={selected ? "page" : undefined}
    className={`shell-nav-item ${selected ? "is-active" : ""}`}>
    <Icon name={icon} size={18} /><span className="shell-nav-label min-w-0 flex-1 truncate">{children}</span>{badge}
  </Link>;
  return link;
}

/** A stable marker explains navigation movement without animating the page or native editor. */
function NavigationIndicator({ scope }: { scope: string }) {
  const marker = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ y: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const nav = marker.current?.parentElement;
    if (!nav) return;
    const update = () => {
      const active = nav.querySelector<HTMLElement>('.shell-nav-item[aria-current="page"]');
      if (!active) return setPosition(null);
      const a = active.getBoundingClientRect(); const n = nav.getBoundingClientRect();
      setPosition({ y: a.top - n.top + nav.scrollTop + 7, height: Math.max(12, a.height - 14) });
    };
    update(); const observer = new ResizeObserver(update); observer.observe(nav);
    return () => observer.disconnect();
  }, [scope]);
  return <span ref={marker} aria-hidden="true" className="shell-selection-indicator"
    style={{ opacity: position ? 1 : 0, height: position?.height ?? 18, transform: `translateY(${position?.y ?? 0}px)` }} />;
}

const ProjectPickerPreview = lazy(async () => {
  const module = await import("@/features/projects/ProjectPreview");
  return { default: module.ProjectPreview };
});

export function ShellFrame({ project, projects = [], projectState = "ready", onRetryProjects, user, unread = 0, notifications = [], notificationState = "ready", onRetryNotifications, children, preview, presentation, breadcrumbs = [], toolbarControls, onSignOut, canViewMonitoring = false, spine = false }: ShellFrameProps) {
  const navigationWidth = usePanelWidth("celia.navigation.width", 204, NAV_RAIL, 320);
  const railViewport = useRailViewport();
  const [navigationMode, setNavigationMode] = useState<NavigationMode>(() => {
    try { const saved = localStorage.getItem("celia.navigation.mode"); return saved === "icons" || saved === "expanded" ? saved : "auto"; } catch { return "auto"; }
  });
  useEffect(() => { try { localStorage.setItem("celia.navigation.mode", navigationMode); } catch { /* Optional preference. */ } }, [navigationMode]);
  const rail = navigationMode === "icons" || navigationMode === "auto" && (navigationWidth.width < NAV_TEXT_MIN || railViewport);
  const platform = !project && !preview;
  const location = useLocation();
  const isAdministration = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  // Platform pages have no sidebar; their destinations stay one click away in the top bar.
  const platformDestinations = [
    { to: "/", label: "Overview", end: true }, { to: "/projects", label: "Projects" }, { to: "/models", label: "Models" },
    ...(isAdministration && (user.is_admin || canViewMonitoring) ? [{ to: user.is_admin ? "/admin" : "/admin?tab=monitoring", label: user.is_admin ? "Administration" : "Resource dashboard" }] : []),
  ];
  const navigate = useNavigate();
  const candidateNavigation = location.pathname.startsWith("/uploads/") ? new URLSearchParams(location.search).get("from") === "reviews" ? "reviews" : "uploads" : undefined;
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [navigationHidden, setNavigationHidden] = useState(() => {
    try { return localStorage.getItem("celia.navigation.hidden") === "true"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("celia.navigation.hidden", String(navigationHidden)); } catch { /* Storage may be unavailable. */ }
  }, [navigationHidden]);
  const tipsOn = useTipsEnabled();
  const [signOutBusy, setSignOutBusy] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const navigation = useRef<HTMLDialogElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const revealNavigationButton = useRef<HTMLButtonElement>(null);
  const hideNavigationButton = useRef<HTMLButtonElement>(null);
  const navigationFocusPending = useRef(false);
  function changeNavigationVisibility(hidden: boolean) {
    navigationFocusPending.current = true;
    setNavigationHidden(hidden);
  }
  useLayoutEffect(() => {
    if (!navigationFocusPending.current) return;
    navigationFocusPending.current = false;
    (navigationHidden ? revealNavigationButton : hideNavigationButton).current?.focus();
  }, [navigationHidden]);
  const id = useId();
  const slug = project?.slug ?? "";
  const projectBase = `/p/${encodeURIComponent(slug)}`;
  useEffect(() => { if (slug && !preview) rememberProject(slug); }, [slug, preview]);
  const projectOptions = orderProjects(project && !projects.some((item) => item.slug === slug) ? [project, ...projects] : projects, [slug, ...recentProjects()]);
  const section = routeSection(location.pathname);
  const inferredLabel = section?.label ?? "Overview";
  const frameBreadcrumbs = spine && project && breadcrumbs.length === 1
    ? [...breadcrumbs, { label: inferredLabel, emphasis: true }]
    : breadcrumbs.length ? breadcrumbs : project
      ? [{ label: project.name, to: projectBase }, { label: inferredLabel, emphasis: true }]
      : [];

  useEffect(() => { setNavigationOpen(false); }, [location.pathname, location.search]);
  // Animate the new route without remounting it or forcing a synchronous layout read.
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = main.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const animation = element.animate([{ opacity: .6, transform: "translateY(4px)" }, { opacity: 1, transform: "none" }],
      { duration: 180, easing: "cubic-bezier(.2,.8,.2,1)" });
    return () => animation.cancel();
  }, [location.pathname]);
  useEffect(() => {
    const dialog = navigation.current;
    if (!dialog) return;
    if (navigationOpen && !dialog.open) dialog.showModal();
    if (!navigationOpen && dialog.open) dialog.close();
  }, [navigationOpen]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setNavigationOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  function sidebar(mobile: boolean) {
    return <>
      <div className="shell-brand-block">
        <div className={`shell-brand-row flex items-center justify-between gap-3 ${presentation === "upload-preparation" ? "upload-preparation-shell-brand-row" : ""}`}>
          <Link to={preview?.home ?? "/"} className="shell-brand"><CeliaBrand /></Link>
          {mobile
            ? <button type="button" className="icon-button shell-navigation-toggle" aria-label="Close navigation" title="Close navigation" onClick={() => setNavigationOpen(false)}><Glyph name="menu_open" size={20} /></button>
            : <button ref={hideNavigationButton} type="button" className="icon-button shell-navigation-toggle shell-desktop-navigation-toggle" aria-label="Hide navigation" title="Hide navigation" aria-expanded={!navigationHidden} aria-controls={`${id}-sidebar`} onClick={() => changeNavigationVisibility(true)}><Glyph name="menu_open" size={20} /></button>}
        </div>
        <span className="shell-project-caption">Current project</span>
        <div className="shell-project-select celia-project-picker" data-unselected={!project || undefined}>
          <Select id={`${id}-${mobile ? "mobile" : "desktop"}-project`} label="Project" value={slug}
            options={projectOptions.map((item) => ({ value: item.slug, label: item.name,
              description: [item.slug, item.description?.trim()].filter(Boolean).join(" · ") }))}
            className="project-visual-picker"
            placeholder="Select a project…" searchable searchPlaceholder="Find a project…"
            loading={projectState === "loading"} error={projectState === "error" ? "Projects are unavailable." : undefined} onRetry={onRetryProjects}
            disabled={Boolean(preview)} disabledReason="Project switching is unavailable in the design preview."
            icon={project ? <ProjectMosaic slug={project.slug} compact /> : <Icon name="project" size={18} />}
            renderOption={(option, state) => <span className="project-picker-identity"><ProjectMosaic slug={option.value} compact /><span className="project-picker-option-copy" data-current={state.selected || undefined}>
              <span>{option.label}</span><small>{state.selected ? "Current project" : option.value}</small>
            </span></span>}
            renderPreview={(option) => {
              const item = projectOptions.find((candidate) => candidate.slug === option?.value);
              return item ? <Suspense fallback={<div className="project-picker-preview-loading">Loading image preview…</div>}>
                <ProjectPickerPreview key={item.slug} slug={item.slug} name={item.name} description={item.description} layout="picker" />
              </Suspense> : <div className="project-picker-preview-loading">Choose a project.</div>;
            }}
            onChange={(next) => { if (!preview) navigate(next ? `/p/${encodeURIComponent(next)}` : "/"); }}
            footer={preview ? undefined : { label: "Manage projects", onClick: () => navigate("/projects") }} />
        </div>
      </div>
      <nav aria-label="Main navigation" className="shell-navigation" data-testid="project-tabs">
        <NavigationIndicator scope={location.pathname + String(preview?.active)} />
        {project && <>
          {!preview && <Item to={projectBase} end icon="overview">Overview</Item>}
          {preview && presentation === "upload-preparation" && <Item to={preview.home} end icon="overview" active={false}>Project overview</Item>}
          <section aria-label="Data" className="shell-nav-section">
            <h2 className="shell-nav-heading">Data</h2>
            {DATA_NAV.map((item) => {
              const to = destination(item, slug, preview?.links);
              return to && <Item key={item.id} to={to} icon={item.icon}
                badge={navigationBadge(item.badge)}
                active={preview ? preview.active === item.id : candidateNavigation ? item.id === candidateNavigation : section?.nav ? section.nav === item.id : undefined}>
                {item.label}
              </Item>;
            })}
          </section>
          <section aria-label="Prepare & manage data" className="shell-nav-section">
            <h2 className="shell-nav-heading">Prepare &amp; manage data</h2>
            {PREPARE_NAV.map((item) => {
              const to = destination(item, slug, preview?.links);
              return to && <Item key={item.id} to={to} icon={item.icon}
                badge={navigationBadge(item.badge)}
                active={preview ? preview.active === item.id : candidateNavigation ? item.id === candidateNavigation : section?.nav ? section.nav === item.id : undefined}>
                {item.label}
              </Item>;
            })}
          </section>
        </>}
        {project && <section aria-label="Model" className="shell-nav-section">
          <h2 className="shell-nav-heading">Model</h2>
          {MODEL_NAV.map((item) => {
            const to = destination(item, slug);
            return to && <Item key={item.id} to={to} icon={item.icon} badge={navigationBadge(item.badge)}
              active={section?.nav ? section.nav === item.id : undefined}>{item.label}</Item>;
          })}
        </section>}
        {project && <section aria-label="Operations" className="shell-nav-section">
          <h2 className="shell-nav-heading">Operations</h2>
          {OPERATIONS_NAV.filter((item) => !preview || (item.id !== "workflows" && item.id !== "jobs")).map((item) => {
            const to = destination(item, slug, preview?.links);
            return to && <Item key={item.id} to={to} icon={item.icon}
              badge={navigationBadge(item.badge)}
              description={item.description}
              active={preview ? preview.active === item.id : candidateNavigation ? item.id === candidateNavigation : section?.nav ? section.nav === item.id : undefined}>
              {item.label}
            </Item>;
          })}
        </section>}
      </nav>
      {!mobile && <div className="shell-navigation-preferences">
        <ToolbarPopover label="Sidebar layout" triggerClass="shell-navigation-preferences-trigger" triggerContent={<><Glyph name="view_sidebar" size={18} /><span>Sidebar</span><Glyph name="tune" size={15} /></>} menu role="menu" closeKey={navigationMode}>
          <div className="toolbar-popover-heading"><strong>Sidebar layout</strong><small>Icon mode expands on hover or keyboard focus.</small></div>
          {([["auto", "Auto", "Match the window width", "responsive_layout"], ["expanded", "Expanded", "Keep navigation labels visible", "side_navigation"], ["icons", "Icons", "More room for your workspace", "view_sidebar"]] as const).map(([mode, label, description, icon]) =>
            <button key={mode} type="button" role="menuitemradio" aria-checked={navigationMode === mode} className="toolbar-menu-action" onClick={() => { setNavigationMode(mode); if (mode !== "icons" && navigationWidth.width < NAV_TEXT_MIN) navigationWidth.setWidth(232); }}>
              <Glyph name={icon} size={18} /><span className="shell-navigation-mode-copy"><strong>{label}</strong><small>{description}</small></span>{navigationMode === mode && <Glyph name="check" size={16} />}
            </button>)}
        </ToolbarPopover>
      </div>}
    </>;
  }

  return <IconFamily.Provider value={presentation === "upload-preparation" || spine ? "material" : "legacy"}><div data-platform={platform || undefined} data-navigation-hidden={!platform && navigationHidden || undefined} data-navigation-rail={!platform && rail || undefined} className={`shell-frame ${presentation === "upload-preparation" ? "upload-preparation-shell" : ""} ${spine ? "shell-spine-v2" : ""}`}>
    <a href="#main-content" className="skip-link">Skip to content</a>
    {!platform && <><aside id={`${id}-sidebar`} className="shell-sidebar" style={navigationWidth.style}>{!navigationOpen && sidebar(false)}{navigationMode !== "icons" && <PanelResize panel={navigationMode === "expanded" ? { ...navigationWidth, min: NAV_TEXT_MIN } : navigationWidth} side="right" label="Resize navigation" snap={navigationMode === "auto" ? snapNavigation : undefined} />}</aside>
    <dialog ref={navigation} id={`${id}-navigation`} className="shell-mobile-navigation" aria-label="Navigation"
      onCancel={(event) => { event.preventDefault(); setNavigationOpen(false); }}
      onClose={() => { setNavigationOpen(false); menuButton.current?.focus(); }}>
      {navigationOpen && sidebar(true)}
    </dialog></>}
    <div className="shell-main">
      <header className="shell-toolbar">
        {platform ? <div className="platform-shell-brand"><Link to="/" aria-label="Celia home"><CeliaBrand /></Link>
          <nav className="platform-shell-nav" aria-label="Platform">
            {platformDestinations.map(({ to, label, end }) => {
              const path = to.split("?")[0];
              const current = end ? location.pathname === path : location.pathname === path || location.pathname.startsWith(`${path}/`);
              return <Link key={to} to={to} aria-current={current ? "page" : undefined}>{label}</Link>;
            })}
          </nav></div> : presentation === "upload-preparation" || spine ? <nav className="upload-preparation-shell-breadcrumb" aria-label="Breadcrumbs">
          {navigationHidden && <button ref={revealNavigationButton} type="button" className="icon-button shell-navigation-toggle shell-desktop-navigation-toggle" title="Show navigation"
            aria-label="Show navigation" aria-expanded={false}
            aria-controls={`${id}-sidebar`} onClick={() => changeNavigationVisibility(false)}><Glyph name="menu" size={20} /></button>}
          <button ref={menuButton} type="button" className="icon-button shell-navigation-toggle lg:hidden" aria-label="Open navigation" title="Open navigation"
            aria-expanded={navigationOpen} aria-controls={`${id}-navigation`} onClick={() => setNavigationOpen(true)}><Glyph name="menu" size={20} /></button>
          {frameBreadcrumbs.map((item, index) => <Fragment key={`${index}-${item.label}`}>
            {index > 0 && <Glyph name="chevron_right" size={14} className="shell-crumb-separator" />}
            {item.to ? <Link to={item.to} title={item.label}>{item.label}</Link> : item.emphasis ? <strong aria-current={index === frameBreadcrumbs.length - 1 ? "page" : undefined} title={item.label}>{item.label}</strong> : <span>{item.label}</span>}
          </Fragment>)}
        </nav> : <div className="flex min-w-0 items-center gap-3">
          {navigationHidden && <button ref={revealNavigationButton} type="button" className="icon-button shell-navigation-toggle shell-desktop-navigation-toggle" title="Show navigation"
            aria-label="Show navigation" aria-expanded={false}
            aria-controls={`${id}-sidebar`} onClick={() => changeNavigationVisibility(false)}><Glyph name="menu" size={20} /></button>}
          <button ref={menuButton} type="button" className="icon-button shell-navigation-toggle lg:hidden" aria-label="Open navigation" title="Open navigation"
            aria-expanded={navigationOpen} aria-controls={`${id}-navigation`} onClick={() => setNavigationOpen(true)}><Glyph name="menu" size={20} /></button>
          <Icon name="project" size={16} className="hidden text-muted sm:block" />
          <span className="truncate text-sm font-medium text-ink">{project?.name ?? "Workspace"}</span>
          {preview && <span className="shell-preview-label">Design preview</span>}
        </div>}
        <div className="flex shrink-0 items-center gap-2">
          {import.meta.env.DEV && import.meta.env.VITE_CELIA_PREVIEW_LABEL && <span className="shell-preview-label" title="This local preview reads a snapshot of the real platform. Writes and training submission are disabled.">{import.meta.env.VITE_CELIA_PREVIEW_LABEL}</span>}
          {toolbarControls}
          <AppearanceToggle />
          <ToolbarPopover label="Notifications" closeKey={location.pathname + location.search}
            triggerClass="icon-button toolbar-notification-button" triggerContent={<>
              <span className="ui-glyph" aria-hidden="true">notifications</span>
              {unread > 0 && <span className="toolbar-notification-count" data-testid="unread">{unread > 99 ? "99+" : unread}</span>}
            </>}>
            <div className="toolbar-popover-heading"><strong>Notifications</strong><small>{notificationState === "ready" ? `${unread.toLocaleString()} unread` : "Unread count unavailable"}</small></div>
            {notificationState === "loading" ? <p className="toolbar-popover-message" role="status">Loading notifications…</p>
              : notificationState === "error" ? <div className="toolbar-popover-message" role="status">Notifications are unavailable.
                {onRetryNotifications && <button type="button" onClick={onRetryNotifications} className="toolbar-menu-action">Retry</button>}</div>
              : notifications.length === 0 ? <p className="toolbar-popover-message">No unread notifications.</p>
              : notifications.slice(0, 8).map((item) => item.link
                ? <Link key={item.id} to={item.link} className="toolbar-notification-link" data-severity={item.severity}><Icon name="inbox" size={16} /><span>{item.title}
                    {item.detail && <small className="toolbar-notification-detail">{item.detail}</small>}<small title={exactTime(item.created_at)}>{relativeTime(item.created_at)}</small></span></Link>
                : <div key={item.id} className="toolbar-notification-item">{item.title}<small title={exactTime(item.created_at)}>{relativeTime(item.created_at)}</small></div>)}
            <div className="toolbar-popover-separator" />
            <Link to="/inbox" data-testid="inbox-link"><Icon name="inbox" size={16} />Open inbox</Link>
          </ToolbarPopover>
          <ToolbarPopover label={`Account menu: ${user.username}`} role="menu" menu closeKey={location.pathname + location.search}
            triggerClass="shell-avatar" triggerContent={user.username.slice(0, 2).toUpperCase()}>
            <div className="shell-account-menu">
              <div className="toolbar-popover-heading"><strong>{user.username}</strong><small>{user.is_admin ? "Platform administrator" : "Account"}</small></div>
              <div role="group" aria-label="Personal">
                <Link role="menuitem" to="/settings"><Icon name="account" size={16} /><span>Account settings</span></Link>
                <Link role="menuitem" to="/inbox"><Icon name="inbox" size={16} /><span>Inbox</span>{unread > 0 && <span className="shell-unread">{unread > 99 ? "99+" : unread}</span>}</Link>
              </div>
              <div className="shell-account-menu-group" role="group" aria-labelledby={`${id}-account-workspace`}>
                <div className="shell-account-menu-label" id={`${id}-account-workspace`}>Workspace</div>
                <Link role="menuitem" to="/projects"><Icon name="project" size={16} /><span>All projects</span></Link>
                {project && !preview && <Link role="menuitem" to={`${projectBase}/settings`}><Icon name="settings" size={16} /><span>Project settings</span></Link>}
                {(user.is_admin || canViewMonitoring) && <Link role="menuitem" to={user.is_admin ? "/admin" : "/admin?tab=monitoring"}><Icon name="settings" size={16} /><span>{user.is_admin ? "Admin" : "Resource dashboard"}</span></Link>}
              </div>
              <div className="shell-account-menu-group" role="group" aria-labelledby={`${id}-account-help`}>
                <div className="shell-account-menu-label" id={`${id}-account-help`}>Help</div>
                <Link role="menuitem" to="/docs/cli"><Icon name="file" size={16} /><span>CLI documentation</span></Link>
                <button type="button" role="menuitem" className="toolbar-menu-action" onClick={resetTips} data-testid="show-tips"><span className="ui-glyph" aria-hidden="true">lightbulb</span><span>{tipsOn ? "Show all tips again" : "Turn tips back on"}</span></button>
              </div>
              <div className="toolbar-popover-separator" role="separator" />
              <button type="button" role="menuitem" className="toolbar-menu-action" disabled={signOutBusy || !onSignOut || Boolean(preview)} onClick={async () => {
                if (!onSignOut) return;
                setSignOutBusy(true); setSignOutError(false);
                try { await onSignOut(); } catch { setSignOutError(true); } finally { setSignOutBusy(false); }
              }}><span className="ui-glyph" aria-hidden="true">logout</span>{signOutBusy ? "Logging out…" : "Logout"}</button>
              {signOutError && <p role="alert" className="toolbar-popover-message is-error">Could not log out. Please try again.</p>}
            </div>
          </ToolbarPopover>
        </div>
      </header>
      <main ref={main} id="main-content" tabIndex={-1} className="shell-content">{children}</main>
    </div>
  </div></IconFamily.Provider>;
}

export function Shell() {
  const {store:downloads}=useDownloads();
  const { pathname, search } = useLocation();
  const me = api.useQuery("get", "/api/me");
  const signedIn = Boolean(me.data);
  const dashboard = api.useQuery("get", "/api/admin/monitoring/access/me", {}, { enabled: signedIn, staleTime: 0 });
  const projects = api.useQuery("get", "/api/projects", {}, { enabled: signedIn });
  const inbox = api.useQuery("get", "/api/inbox", { params: { query: { unread: true } } }, { enabled: signedIn, refetchInterval: 60_000, refetchOnWindowFocus: true });
  const slug = /^\/p\/([^/]+)/.exec(pathname)?.[1];
  const jobId = /^\/jobs\/([^/]+)/.exec(pathname)?.[1] ?? "";
  // Share the generated query cache with the detail pages. Never infer the project
  // of a global object from the last project visited.
  const job = api.useQuery("get", "/api/jobs/{job_id}", { params: { path: { job_id: jobId } } }, { enabled: signedIn && Boolean(jobId) });
  const project = slug
    ? projects.data?.projects.find((item) => item.slug === slug) ?? { slug, name: slug }
    : jobId ? projects.data?.projects.find((item) => item.id === job.data?.job.project_id) : undefined;
  const isNewUpload = /^\/p\/[^/]+\/uploads\/new\/?$/.test(pathname);
  const isUploadSession = /^\/p\/[^/]+\/upload-sessions\/[^/]+\/?$/.test(pathname);
  const isUploads = /^\/p\/[^/]+\/uploads\/?$/.test(pathname);
  const isDatasets = /^\/p\/[^/]+\/datasets\/?$/.test(pathname);
  const versionPath = /^\/p\/[^/]+\/datasets\/[^/]+\/v\/[^/]+\/?$/.test(pathname);
  const versionTab = new URLSearchParams(search).get("tab");
  const isLabeling = versionPath && (versionTab === "samples" || versionTab === "labelling");
  const isStatistics = versionPath && ["stats", "taxonomy", "leakage"].includes(versionTab ?? "");
  const isReviews = /^\/p\/[^/]+\/reviews\/?$/.test(pathname);
  const isCompare = /^\/p\/[^/]+\/datasets\/[^/]+\/compare\/?$/.test(pathname);
  const isAudit = /^\/p\/[^/]+\/audit\/?$/.test(pathname);
  const isLineage = /^\/p\/[^/]+\/lineage\/?$/.test(pathname);
  const isWorkflow = /^\/p\/[^/]+\/workflows\/?$/.test(pathname);
  const isDeletions = /^\/p\/[^/]+\/deletions\/?$/.test(pathname);
  const isViewBuilder = /^\/p\/[^/]+\/views\/new\/?$/.test(pathname);
  // Page-specific content is still migrated independently, but every route now
  // enters through the same authenticated spine. This keeps navigation, theme,
  // icon vocabulary and responsive behavior consistent during the page rollout.
  const isInference = /^\/p\/[^/]+\/inference\/?$/.test(pathname);
  const compact = isInference || isNewUpload || isUploadSession || isLabeling || isAudit || isCompare || isReviews || isStatistics || isLineage || isWorkflow || isDeletions || isViewBuilder || isUploads || isDatasets;
  const projectLink = project ? `/p/${encodeURIComponent(project.slug)}` : undefined;
  const breadcrumbs: Breadcrumb[] = project ? [{ label: project.name, to: projectLink }] : [];
  if (slug && projectLink) breadcrumbs.push(...projectTrail(pathname, search, projectLink));
  else if (jobId && projectLink) breadcrumbs.push({ label: "Jobs & Runs", to: `${projectLink}/jobs` }, { label: job.data?.job.type ?? jobId.slice(0, 8), emphasis: true });
  // The platform home is separate from the project directory and project overview.
  if (pathname === "/") breadcrumbs.push({ label: "Platform" }, { label: "Overview", emphasis: true });
  if (pathname === "/admin") {
    const sections: Record<string, string> = { overview: "Overview", monitoring: "Monitoring", deletions: "Deletions", storage: "Storage", tasks: "Tasks", sync: "Sync", accounts: "Accounts" };
    const section = sections[new URLSearchParams(search).get("tab") ?? "overview"] ?? "Overview";
    breadcrumbs.push({ label: me.data?.user.is_admin ? "Platform administration" : "Resource dashboard", to: "/admin" }, { label: section, emphasis: true });
  } else if (pathname.startsWith("/admin/monitoring/")) {
    // The trail comes from the address alone; the page header carries the parent links its reply names.
    const [, , , kind = "", id = ""] = pathname.split("/");
    const kinds: Record<string, string> = { hosts: "Hosts", nodes: "Nodes", gpus: "GPUs" };
    breadcrumbs.push({ label: me.data?.user.is_admin ? "Platform administration" : "Resource dashboard", to: "/admin" },
      { label: "Monitoring", to: "/admin?tab=monitoring&monitoring_view=fleet" }, { label: kinds[kind] ?? "Monitoring" },
      { label: decodeURIComponent(id), emphasis: true });
  } else if (pathname.startsWith("/docs/cli")) {
    breadcrumbs.push({ label: "Documentation", to: "/docs/cli" }, { label: "Celia CLI", emphasis: true });
  } else if (!project && pathname !== "/") {
    breadcrumbs.push({ label: "Platform", to: "/" }, { label: (pathname.startsWith("/model-factory") || pathname.startsWith("/models")) ? "Models" : pathname.startsWith("/inbox") ? "Notifications" : pathname.startsWith("/settings") ? "Account" : pathname.startsWith("/projects") ? "Projects" : "Workspace", emphasis: true });
  }
  if (me.error) return <div className="p-6"><Failed error={me.error} onRetry={() => me.refetch()} /></div>;
  if (!me.data) return <Loading />;
  return <ShellFrame canViewMonitoring={dashboard.data?.can_view} project={project} projects={projects.data?.projects} user={me.data.user} unread={inbox.data?.unread}
    projectState={projects.isError ? "error" : projects.data ? "ready" : "loading"} onRetryProjects={() => { void projects.refetch(); }}
    notifications={inbox.data?.items} notificationState={inbox.isError ? "error" : inbox.data ? "ready" : "loading"}
    onRetryNotifications={() => { void inbox.refetch(); }}
    spine={!compact}
    presentation={compact ? "upload-preparation" : undefined}
    breadcrumbs={breadcrumbs}
    onSignOut={async () => {
      const data = await body(fetchClient.POST("/api/auth/logout"));
      downloads.clear();
      location.assign(data?.logout_url ?? "/login");
    }}><PortalRecovery key={me.data.user.id} actorId={me.data.user.id}>{isLabeling || isStatistics || isUploads || isDatasets ? <div className="data-workspace-document"><Outlet /></div> : <Outlet />}</PortalRecovery></ShellFrame>;
}
